import type { AutomationLane, AutomationPoint, AutomationTarget, Project } from '@/domain/types';
import { curveShape } from '@/domain/curves';
import { ticksToSeconds, type NormalisedTempoMap } from '@/timing/tempoMap';
import type { AutomatableParam } from './effects/EffectNode';
import { instrumentDescriptor, instrumentParamDescriptorFor } from './instruments/descriptors';
import {
  effectDescriptor,
  denormaliseParam,
  isAutomatable,
  normaliseParam,
  effectParamDescriptor,
  trackParamDescriptor,
  type ParamDescriptor,
} from './effects/descriptors';

/**
 * Automation, as data.
 *
 * Lane points store **normalised** values in 0..1. Real parameter values come
 * from the parameter's own descriptor, which is also what the fader, the
 * inspector and the file parser use — so a lane drawn at half height means the
 * same thing everywhere, and a curve stays meaningful if a parameter's range is
 * ever widened.
 *
 * Evaluation and event generation are pure functions over a lane. Writing those
 * events onto an `AudioParam` is a separate, mechanical step (`applyAutomation`),
 * which is what lets the interesting part — curve shapes, segments that begin
 * before the render does, loop boundaries — be tested without a browser.
 */

/** Resolves the descriptor for an automation target, or null if it cannot be automated. */
export function resolveParamDescriptor(project: Project, target: AutomationTarget): ParamDescriptor | null {
  if (target.kind === 'track') {
    const descriptor = trackParamDescriptor(target.paramId);
    return descriptor && isAutomatable(descriptor) ? descriptor : null;
  }
  if (target.kind === 'effect') {
    const track = project.tracks[target.trackId];
    const effect = track?.effects.find((e) => e.id === target.effectId);
    if (!effect) return null;
    const descriptor = effectParamDescriptor(effect, target.paramId);
    return descriptor && isAutomatable(descriptor) ? descriptor : null;
  }
  if (target.kind === 'instrument') {
    const instrument = project.tracks[target.trackId]?.instrument;
    if (!instrument) return null;
    const descriptor = instrumentParamDescriptorFor(instrument, target.paramId);
    return descriptor && isAutomatable(descriptor) ? descriptor : null;
  }
  return null;
}

/** Human-readable lane name, e.g. `Volume` or `Delay · Feedback`. */
export function describeTarget(project: Project, target: AutomationTarget): string {
  if (target.kind === 'track') return trackParamDescriptor(target.paramId)?.label ?? target.paramId;
  if (target.kind === 'instrument') {
    const instrument = project.tracks[target.trackId]?.instrument;
    if (!instrument) return target.paramId;
    const descriptor = instrumentDescriptor(instrument);
    const param = descriptor.params.find((p) => p.id === target.paramId);
    return `${descriptor.label} · ${param?.label ?? target.paramId}`;
  }
  const effect = project.tracks[target.trackId]?.effects.find((e) => e.id === target.effectId);
  if (!effect) return target.paramId;
  const descriptor = effectDescriptor(effect);
  const param = descriptor.params.find((p) => p.id === target.paramId);
  return `${descriptor.label} · ${param?.label ?? target.paramId}`;
}

/** Points sorted by position. Lanes are kept sorted by the commands, but loading is defensive. */
function sortedPoints(lane: AutomationLane): AutomationPoint[] {
  for (let i = 1; i < lane.points.length; i++) {
    if (lane.points[i].tick < lane.points[i - 1].tick) return [...lane.points].sort((a, b) => a.tick - b.tick);
  }
  return lane.points as AutomationPoint[];
}

/**
 * Normalised lane value at a position.
 *
 * Before the first point and after the last, the value is held — an automation
 * lane describes the whole timeline, not just the span between its points, and
 * a lane that silently reverted to the static value outside its points would be
 * impossible to reason about.
 */
export function automationValueAt(lane: AutomationLane, tick: number): number | null {
  const points = sortedPoints(lane);
  if (points.length === 0) return null;
  if (tick <= points[0].tick) return points[0].value;

  const last = points[points.length - 1];
  if (tick >= last.tick) return last.value;

  let lo = 0;
  let hi = points.length - 1;
  while (lo < hi - 1) {
    const mid = (lo + hi) >> 1;
    if (points[mid].tick <= tick) lo = mid;
    else hi = mid;
  }

  const a = points[lo];
  const b = points[lo + 1];
  const span = b.tick - a.tick;
  if (span <= 0) return b.value;
  return a.value + (b.value - a.value) * curveShape(a.curve, (tick - a.tick) / span);
}

/** Real (denormalised) value of a lane at a position, or null when the lane is empty. */
export function automationRealValueAt(
  lane: AutomationLane,
  descriptor: ParamDescriptor,
  tick: number,
): number | null {
  const normalised = automationValueAt(lane, tick);
  return normalised === null ? null : denormaliseParam(descriptor, normalised);
}

export interface AutomationEvent {
  tick: number;
  /** Normalised 0..1. */
  value: number;
  /**
   * `step` sets the value instantly; `ramp` interpolates linearly from the
   * previous event. Smooth segments are emitted as a series of short ramps,
   * which is how a curve becomes something an AudioParam can follow.
   */
  kind: 'step' | 'ramp';
}

/** Sub-ramps used to approximate one smooth segment. */
const SMOOTH_STEPS = 24;

/** Sub-ramps used when a parameter's value mapping is not a straight line. */
const NONLINEAR_STEPS = 32;

/**
 * Flattens a lane into events covering `[fromTick, toTick]`.
 *
 * The first event is always an anchor at `fromTick` carrying the value the lane
 * already holds there, so a render or a playback pass that starts mid-segment
 * begins at the right value rather than jumping to the next point.
 */
export function automationEvents(lane: AutomationLane, fromTick: number, toTick: number): AutomationEvent[] {
  const points = sortedPoints(lane);
  if (points.length === 0) return [];

  const start = automationValueAt(lane, fromTick);
  if (start === null) return [];

  const events: AutomationEvent[] = [{ tick: fromTick, value: start, kind: 'step' }];
  if (toTick <= fromTick) return events;

  for (let i = 0; i < points.length; i++) {
    const point = points[i];
    if (point.tick <= fromTick) continue;
    if (point.tick > toTick) break;

    const previous = points[i - 1];
    const curve = previous?.curve ?? 'linear';

    if (!previous || curve === 'hold') {
      // Hold: keep the old value right up to the point, then jump.
      events.push({ tick: point.tick, value: point.value, kind: 'step' });
      continue;
    }

    if (curve === 'smooth') {
      const segStart = Math.max(previous.tick, fromTick);
      const segSpan = point.tick - segStart;
      for (let s = 1; s <= SMOOTH_STEPS; s++) {
        const tick = segStart + (segSpan * s) / SMOOTH_STEPS;
        const value = automationValueAt(lane, tick);
        if (value !== null) events.push({ tick, value, kind: 'ramp' });
      }
      continue;
    }

    events.push({ tick: point.tick, value: point.value, kind: 'ramp' });
  }

  // Hold the final value to the end of the span, so a parameter does not drift
  // back to a stale scheduled value after the last point.
  const lastEvent = events[events.length - 1];
  if (lastEvent.tick < toTick) {
    events.push({ tick: toTick, value: lastEvent.value, kind: 'ramp' });
  }

  return events;
}

/**
 * Keys of the parameters an automation lane currently owns on a track, so
 * static writes can stand aside. Channel-strip parameters are keyed by their
 * id; effect parameters by `effectId:paramId`.
 */
export function automatedTrackParams(project: Project, trackId: string): Set<string> {
  const out = new Set<string>();
  const track = project.tracks[trackId];
  if (!track) return out;
  for (const laneId of track.automationLaneIds) {
    const lane = project.automation[laneId];
    if (!lane || !lane.enabled || lane.points.length === 0) continue;
    if (lane.target.kind === 'track') out.add(lane.target.paramId);
    else if (lane.target.kind === 'instrument') out.add(`instrument:${lane.target.paramId}`);
    else if (lane.target.kind === 'effect' && lane.target.effectId) {
      out.add(`${lane.target.effectId}:${lane.target.paramId}`);
    }
  }
  return out;
}

/** Every enabled, non-empty lane in the project, in track order. */
export function activeLanes(project: Project): AutomationLane[] {
  const lanes: AutomationLane[] = [];
  for (const trackId of [...project.trackOrder, project.masterTrackId]) {
    const track = project.tracks[trackId];
    if (!track) continue;
    for (const laneId of track.automationLaneIds) {
      const lane = project.automation[laneId];
      if (lane && lane.enabled && lane.points.length > 0) lanes.push(lane);
    }
  }
  return lanes;
}

// ---------------------------------------------------------------------------
// Writing automation onto AudioParams
// ---------------------------------------------------------------------------

/** Maps a project-seconds position onto a context time, or null if unscheduled. */
export type TimeResolver = (projectSeconds: number) => number | null;

/**
 * Schedules one lane onto its AudioParams for a span of the timeline.
 *
 * The whole span is written in one pass rather than topped up window by window.
 * Automation event counts are small — hundreds, not millions — so scheduling
 * the lot is cheap, and it avoids the trap of re-anchoring a long ramp every
 * 25ms, which would turn a smooth eight-bar fade into a staircase.
 */
export function writeLaneAutomation(
  targets: readonly AutomatableParam[],
  lane: AutomationLane,
  descriptor: ParamDescriptor,
  fromTick: number,
  toTick: number,
  tempoMap: NormalisedTempoMap,
  timeFor: TimeResolver,
): void {
  if (targets.length === 0) return;

  // An AudioParam ramp interpolates linearly in the parameter's own units. That
  // is exact for a linear parameter, but wrong for one that is not: ramping a
  // filter cutoff from 20kHz to 20Hz in a straight line spends most of its
  // travel in a range the ear barely registers, and the sweep the user drew
  // arrives all at once at the end. Where the mapping bends — log-scaled
  // parameters, stepped ones, an equal-power mix — the segment is sampled
  // along the way instead.
  const bends =
    descriptor.scale === 'log' || descriptor.step !== undefined || targets.some((t) => t.nonlinear === true);

  const events = bends
    ? subdivideRamps(lane, automationEvents(lane, fromTick, toTick), NONLINEAR_STEPS)
    : automationEvents(lane, fromTick, toTick);
  if (events.length === 0) return;

  const anchorTime = timeFor(ticksToSeconds(tempoMap, fromTick));
  if (anchorTime === null) return;

  for (const target of targets) {
    try {
      target.param.cancelScheduledValues(anchorTime);
    } catch {
      // Some implementations reject a cancel in the past; the explicit first
      // event below still puts the parameter where it belongs.
    }

    for (const event of events) {
      const time = timeFor(ticksToSeconds(tempoMap, event.tick));
      if (time === null) continue;
      const value = target.map(denormaliseParam(descriptor, event.value));
      if (!Number.isFinite(value)) continue;
      try {
        if (event.kind === 'step') target.param.setValueAtTime(value, time);
        else target.param.linearRampToValueAtTime(value, time);
      } catch {
        // A rejected event (a time slightly in the past, a param at its limit)
        // must not abort the rest of the lane.
        target.param.value = value;
      }
    }
  }
}

/** Samples each ramp segment so a curved value mapping is followed, not chorded. */
function subdivideRamps(lane: AutomationLane, events: AutomationEvent[], steps: number): AutomationEvent[] {
  if (events.length < 2) return events;
  const out: AutomationEvent[] = [events[0]];

  for (let i = 1; i < events.length; i++) {
    const previous = events[i - 1];
    const event = events[i];
    const span = event.tick - previous.tick;
    if (event.kind !== 'ramp' || span <= 0) {
      out.push(event);
      continue;
    }
    for (let s = 1; s < steps; s++) {
      const tick = previous.tick + (span * s) / steps;
      const value = automationValueAt(lane, tick);
      if (value !== null) out.push({ tick, value, kind: 'ramp' });
    }
    out.push(event);
  }
  return out;
}

/**
 * Sets a lane's parameters to the value the lane holds at a position, with no
 * scheduling. Used when the transport stops or the playhead is moved while
 * stopped, so a parameter stays where the automation left it rather than
 * springing back to the fader position.
 */
export function applyLaneSnapshot(
  targets: readonly AutomatableParam[],
  lane: AutomationLane,
  descriptor: ParamDescriptor,
  tick: number,
  atTime: number,
): void {
  const normalised = automationValueAt(lane, tick);
  if (normalised === null) return;
  const real = denormaliseParam(descriptor, normalised);
  for (const target of targets) {
    const value = target.map(real);
    if (!Number.isFinite(value)) continue;
    try {
      target.param.cancelScheduledValues(atTime);
      target.param.setValueAtTime(value, atTime);
    } catch {
      target.param.value = value;
    }
  }
}

export { normaliseParam, denormaliseParam };
